/**
 * DataManagementPage.jsx
 * ----------------------
 * Main data management interface with tab navigation
 * Access control: Only app admin (creator) can access
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Database, Plus, Settings, AlertCircle } from 'lucide-react';
import CreateDataTab from './CreateDataTab';
import ManageDataTab from './ManageDataTab';
import { useAuthStore } from '../../store/useAuthStore';
import { useAppStore } from '../../store/useAppStore';
import apiService from '../../services/apiService';
import { ProfileIcon, ProfileMenu, ProfileMenuItem } from '../Header/ProfileMenu';
import { FaUserCircle } from 'react-icons/fa';
import { IoSettingsOutline } from 'react-icons/io5';
import { FiLogOut } from 'react-icons/fi';
import Logout from '../Logout/Logut';
import useProfileStore from '../../store/useProfileStore';
import logo from '../../assets/images/newLogo.png';
import './DataManagement.css';

const DataManagementPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appName = searchParams.get('app');
  const { setAppName } = useAppStore();
  const userImage = useProfileStore((state) => state.userImage);
  
  const [activeTab, setActiveTab] = useState('manage');
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [appData, setAppData] = useState(null);
  
  const user = useAuthStore(state => state.user);
  const getUid = useAuthStore(state => state.getUid);

  // Set app name in store when URL parameter changes
  useEffect(() => {
    if (appName) {
      setAppName(appName);
    }
  }, [appName, setAppName]);

  useEffect(() => {
    if (!appName) {
      navigate('/dashboard');
      return;
    }

    const checkAccess = async () => {
      setLoading(true);

      try {
        const uid = getUid?.();
        if (!uid) {
          setAccessDenied(true);
          setLoading(false);
          return;
        }

        // Fetch app data to check ownership
        const apps = await apiService.getAppsByUser(uid);
        const app = Array.isArray(apps)
          ? apps.find(a => a.appName === appName)
          : apps?.data?.find(a => a.appName === appName);

        if (!app) {
          setAccessDenied(true);
          setLoading(false);
          return;
        }

        // Check if user is the app creator
        if (app.createdBy !== uid) {
          setAccessDenied(true);
          setLoading(false);
          return;
        }

        console.log('[DataManagementPage] App data:', app);
        console.log('[DataManagementPage] App prefix:', app.appPrefix || app.appName);

        setAppData(app);
        setAccessDenied(false);
      } catch (error) {
        console.error('Failed to check access:', error);
        setAccessDenied(true);
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [appName, user, navigate, getUid]);

  const handleTableCreated = () => {
    // Switch to manage tab after creating a table
    setActiveTab('manage');
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
        {/* Custom Navigation Bar */}
        <div style={{
          padding: '12px 24px',
          background: '#050618',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          minHeight: '5.25rem',
          position: 'sticky',
          top: 0,
          zIndex: 1000
        }}>
          <div 
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              marginRight: '32px'
            }}
            onClick={() => navigate('/')}
          >
            <img 
              src={logo} 
              alt="MUDUMBAI Logo" 
              style={{
                height: '3.5rem',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginLeft: 'auto'
          }}>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                padding: '8px 14px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span>🏠</span>
              <span>Go to Dashboard</span>
            </button>
          </div>
        </div>
        <div className="data-management-page">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Verifying access...</p>
          </div>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
        {/* Custom Navigation Bar */}
        <div style={{
          padding: '12px 24px',
          background: '#050618',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          minHeight: '5.25rem',
          position: 'sticky',
          top: 0,
          zIndex: 1000
        }}>
          <div 
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              marginRight: '32px'
            }}
            onClick={() => navigate('/')}
          >
            <img 
              src={logo} 
              alt="MUDUMBAI Logo" 
              style={{
                height: '3.5rem',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginLeft: 'auto'
          }}>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                padding: '8px 14px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span>🏠</span>
              <span>Go to Dashboard</span>
            </button>
          </div>
        </div>
        <div className="data-management-page">
          <div className="access-denied">
            <AlertCircle size={64} />
            <h2>Access Denied</h2>
            <p>Only the app owner can manage data tables</p>
            <button onClick={() => navigate('/dashboard')} className="btn-back">
              <ArrowLeft size={16} />
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      {/* Custom Navigation Bar */}
      <div style={{
        padding: '12px 24px',
        background: '#050618',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        minHeight: '5.25rem',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        {/* Left Section: MUDUMBAI Logo */}
        <div 
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            marginRight: '32px'
          }}
          onClick={() => navigate('/')}
        >
          <img 
            src={logo} 
            alt="MUDUMBAI Logo" 
            style={{
              height: '3.5rem',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </div>

        {/* Center Section: Page-specific content */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flex: 1
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              📊
            </div>
            <div>
              <h1 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '700',
                color: '#FFFFFF',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
              }}>
                Data Management
              </h1>
              <p style={{
                margin: 0,
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.8)'
              }}>
                {appName ? `Managing data for: ${appName}` : 'Please select an app from the dashboard'}
              </p>
            </div>
          </div>
        </div>

        {/* Right Section: Create Data, Go to Dashboard and Profile */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          {/* Create Data Button */}
          {appName && (
            <>
              <button
                onClick={() => setActiveTab('create')}
                style={{
                  padding: '8px 14px',
                  background: activeTab === 'create' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255, 255, 255, 0.2)',
                  border: activeTab === 'create' ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  color: '#FFFFFF',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== 'create') {
                    e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'create') {
                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                  }
                }}
              >
                <Plus size={16} />
                <span>Create Data</span>
              </button>
              <button
                onClick={() => setActiveTab('manage')}
                style={{
                  padding: '8px 14px',
                  background: activeTab === 'manage' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255, 255, 255, 0.2)',
                  border: activeTab === 'manage' ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  color: '#FFFFFF',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== 'manage') {
                    e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'manage') {
                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                  }
                }}
              >
                <Settings size={16} />
                <span>Manage Data</span>
              </button>
            </>
          )}

          {/* Go to Dashboard Button */}
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '8px 14px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            }}
          >
            <span>🏠</span>
            <span>Go to Dashboard</span>
          </button>

          {/* Profile Icon */}
          <div style={{ marginLeft: '12px' }}>
            <ProfileMenu
              trigger={
                <div style={{
                  width: '2.75rem',
                  height: '2.75rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                }}
                >
                  <ProfileIcon userImage={userImage} />
                </div>
              }
            >
              <ProfileMenuItem onClick={() => navigate('/userprofile')}>
                <FaUserCircle size={22} /> Profile
              </ProfileMenuItem>
              <ProfileMenuItem onClick={() => navigate('/settings')}>
                <IoSettingsOutline size={22} /> Setting
              </ProfileMenuItem>
              <ProfileMenuItem>
                <FiLogOut size={20} /> <Logout />
              </ProfileMenuItem>
            </ProfileMenu>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="data-management-page" style={{ padding: '24px' }}>
        {!appName ? (
          <div
            style={{
              padding: '40px 20px',
              textAlign: 'center',
              maxWidth: '600px',
              margin: '0 auto',
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '40px',
                border: '2px dashed #E5E7EB',
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
              <h2
                style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                }}
              >
                No App Selected
              </h2>
              <p
                style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  marginBottom: '20px',
                }}
              >
                Please go back to the dashboard and select an app to manage its
                data.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'white',
                  backgroundColor: '#3B82F6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#2563EB';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#3B82F6';
                }}
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Tab Navigation */}
            <div className="tab-navigation" style={{ marginBottom: '24px' }}>
              <button
                onClick={() => setActiveTab('manage')}
                className={`tab-button ${activeTab === 'manage' ? 'active' : ''}`}
              >
                <Settings size={20} />
                Manage Data
              </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
              {activeTab === 'create' && (
                <CreateDataTab
                  appPrefix={appData?.appPrefix || appData?.appName}
                  appId={appData?.appPrefix || appData?.appName}
                  userId={getUid?.()}
                  onTableCreated={handleTableCreated}
                />
              )}
              {activeTab === 'manage' && (
                <ManageDataTab
                  appPrefix={appData?.appPrefix || appData?.appName}
                  appId={appData?.appPrefix || appData?.appName}
                  userId={getUid?.()}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DataManagementPage;


